import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { HomeView } from "@/views/HomeView";
import { CardGameView } from "@/views/CardGameView";
import { GooseBerryView } from "@/views/GooseBerryView";
import { lsGet, lsSet } from "@/lib/storage";
import { scrollToTop } from "@/lib/scroll";

const VIEW_KEY = "synberry-lab.view.v1";
const VIEW_TO_PATH = {
  home: "/",
  "card-game": "/dataliteracy",
  gooseberry: "/gooseberry",
};
const PATH_TO_VIEW = {
  "/": "home",
  "/dataliteracy": "card-game",
  "/gooseberry": "gooseberry",
};

function viewFromPath(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return PATH_TO_VIEW[normalized] || "home";
}

function App() {
  const [view, setView] = useState(() => viewFromPath(window.location.pathname) || lsGet(VIEW_KEY, "home"));
  const [viewLanguage, setViewLanguage] = useState({ "card-game": "nl", gooseberry: "nl" });

  useEffect(() => {
    lsSet(VIEW_KEY, view);
    const nextPath = VIEW_TO_PATH[view] || "/";
    if (window.location.pathname !== nextPath) {
      window.history.pushState({ view }, "", nextPath);
    }
    // Calm scroll-to-top whenever the top-level view changes
    scrollToTop({ delay: 30 });
  }, [view]);

  useEffect(() => {
    const onPopState = () => setView(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const goHome = () => setView("home");
  const openCardGame = () => setView("card-game");
  const openGooseBerry = () => setView("gooseberry");
  const currentLanguage = viewLanguage[view] || "en";
  const setCardGameLanguage = useCallback((language) => {
    setViewLanguage((prev) => (prev["card-game"] === language ? prev : { ...prev, "card-game": language }));
  }, []);
  const setGooseBerryLanguage = useCallback((language) => {
    setViewLanguage((prev) => (prev.gooseberry === language ? prev : { ...prev, gooseberry: language }));
  }, []);

  const context = useMemo(
    () => ({
      home: { label: "Home", subtitle: "Synthetic Users, Design Reflection & Data Literacy" },
      "card-game": { label: "Data Literacy Card Game", subtitle: "Workshop" },
      gooseberry: { label: "GooseBerry", subtitle: "Structured design feedback & dialogue" },
    }),
    []
  );

  return (
    <div className="App" data-testid="app-shell">
      <Header view={view} context={context[view]} onBackHome={goHome} language={currentLanguage} />
      <main className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 pb-16">
        {view === "home" && (
          <HomeView onOpenCardGame={openCardGame} onOpenGooseBerry={openGooseBerry} />
        )}
        {view === "card-game" && <CardGameView onGoHome={goHome} onLanguageChange={setCardGameLanguage} />}
        {view === "gooseberry" && <GooseBerryView onGoHome={goHome} onLanguageChange={setGooseBerryLanguage} />}
      </main>
      <footer className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 pb-10 pt-6 text-xs text-black/60" data-testid="app-footer">
        {currentLanguage === "nl"
          ? "SYNLAB, Ethisch kader: zet mensen voorop, maak technologie begrijpelijk, voorkom ongelijkheid, geef gebruikers controle, voorkom manipulatie."
          : "SYNLAB, Ethical framework: put people first, make technology understandable, avoid inequality, give users control, prevent manipulation."}
      </footer>
      <Toaster />
    </div>
  );
}

export default App;
